import { useEffect } from 'react';
import { lockBodyScroll, unlockBodyScroll } from '../../hooks/useBodyScrollLock';

/**
 * GlobalModalScrollLock
 * ─────────────────────
 * Mount once in App.jsx. Watches the DOM for OPENED overlay elements added
 * AFTER initial paint and locks the body scroll while at least one is open.
 *
 * IMPORTANT design choices to avoid false-positive locks on layout elements:
 *   1. NO initial scan — layout containers (sticky headers, toast roots,
 *      hero overlays) already exist before mount. We only react to nodes
 *      ADDED after the observer starts.
 *   2. Detection is STRICT — an element is treated as an overlay only if
 *      it has an explicit modal hint:
 *        • role="dialog" (and aria-hidden !== "true")
 *        • aria-modal="true"
 *        • data-modal="open"
 *        • data-testid matching /modal|drawer|popup|overlay|sheet/
 *        • class includes "modal" / "drawer" / "popup" / "sheet"
 *        • class includes varo-modal / varo-drawer
 *      `fixed inset-0` alone is NEVER enough — it's used by many layout
 *      helpers (toasts, splash, hero backdrops, etc.).
 *   3. Explicit OPT-OUT via `data-no-scroll-lock` for any node that should
 *      never trigger a lock (toasts, banners, AI chat widget, …).
 */

const MODAL_HINT_REGEX = /(?:^|[\s-])(modal|drawer|popup|overlay|sheet)(?:[\s-]|$)/i;

const hasModalHint = (el) => {
  if (!(el instanceof HTMLElement)) return false;
  if (el.dataset && el.dataset.noScrollLock === 'true') return false;

  // role / aria
  const role = el.getAttribute('role');
  if (role === 'dialog') return el.getAttribute('aria-hidden') !== 'true';
  if (el.getAttribute('aria-modal') === 'true') return true;

  // explicit opt-in data attribute
  if (el.dataset && el.dataset.modal === 'open') return true;

  // data-testid hint (e.g. "kyc-modal", "deposit-drawer")
  const tid = el.getAttribute('data-testid') || '';
  if (tid && MODAL_HINT_REGEX.test(tid)) return true;

  // class-name hint (varo-modal, .modal, .drawer, …)
  const cls = typeof el.className === 'string' ? el.className : '';
  if (cls && MODAL_HINT_REGEX.test(cls)) return true;

  return false;
};

const GlobalModalScrollLock = () => {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const tracked = new Set();

    const trackNode = (node) => {
      if (tracked.has(node)) return;
      if (!hasModalHint(node)) return;
      tracked.add(node);
      lockBodyScroll();
      // Add overscroll-contain to scrollable children to stop scroll-chaining
      try {
        const scrollers = node.querySelectorAll(
          '[class*="overflow-y"], [class*="overflow-auto"], [class*="overflow-scroll"]'
        );
        scrollers.forEach((s) => {
          if (s instanceof HTMLElement && !s.dataset.__voScrollGuard) {
            s.dataset.__voScrollGuard = '1';
            s.style.overscrollBehavior = 'contain';
          }
        });
      } catch (_) { /* noop */ }
    };

    const untrackNode = (node) => {
      if (!tracked.has(node)) return;
      tracked.delete(node);
      unlockBodyScroll();
    };

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        // ADDED nodes (only check the node itself + direct descendants with hint)
        m.addedNodes.forEach((n) => {
          if (!(n instanceof HTMLElement)) return;
          if (hasModalHint(n)) trackNode(n);
          // Also descend in case the modal is wrapped in a portal container
          try {
            const inner = n.querySelectorAll(
              '[role="dialog"], [aria-modal="true"], [data-modal="open"], .varo-modal, .varo-drawer'
            );
            inner.forEach((d) => { if (hasModalHint(d)) trackNode(d); });
          } catch (_) { /* noop */ }
        });

        // REMOVED nodes
        m.removedNodes.forEach((n) => {
          if (!(n instanceof HTMLElement)) return;
          if (tracked.has(n)) untrackNode(n);
          tracked.forEach((tn) => {
            if (n.contains && n.contains(tn)) untrackNode(tn);
          });
        });

        // ATTRIBUTE toggle (e.g. role/aria-hidden/data-modal flipped at runtime)
        if (m.type === 'attributes' && m.target instanceof HTMLElement) {
          const el = m.target;
          const overlayNow = hasModalHint(el);
          const tracked_ = tracked.has(el);
          if (overlayNow && !tracked_) trackNode(el);
          else if (!overlayNow && tracked_) untrackNode(el);
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['role', 'aria-hidden', 'aria-modal', 'data-modal'],
    });

    return () => {
      observer.disconnect();
      // Release any leftover locks on unmount
      tracked.forEach(() => unlockBodyScroll());
      tracked.clear();
    };
  }, []);

  return null;
};

export default GlobalModalScrollLock;
